// Flyout / inline cart and the global AJAX add-to-cart handler.
// Scoped in a function and run once per page: this file can be included by
// more than one section (each section loads what it needs), and a second copy
// would otherwise re-declare its top-level names (SyntaxError) - or, if only
// scoped, bind its document-level listeners a second time.
(() => {
if (window.themeCartDrawerLoaded) return;
window.themeCartDrawerLoaded = true;

// Global (document-level) listeners must only be bound once no matter how
// many <flyout-cart> instances exist on the page (e.g. the header's overlay
// drawer plus a section-level inline cart) - otherwise every add-to-cart
// form submit would be intercepted and POSTed once per instance.
let globalCartListenersBound = false;

function bindGlobalCartListeners() {
  if (globalCartListenersBound) return;
  globalCartListenersBound = true;

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (form.tagName !== 'FORM' || !form.action.includes('/cart/add')) return;
    event.preventDefault();

    // A form can opt out of auto-opening the overlay drawer on add (e.g. the
    // shop-all layout's inline add-to-cart buttons, since the sticky cart
    // panel already shows the result - popping the overlay open too would
    // just be redundant). The drawer stays manually openable regardless.
    const skipOpen = Boolean(form.closest('[data-skip-cart-open]'));

    document.dispatchEvent(new CustomEvent('cart:loading-start'));
    try {
      const formData = new FormData(form);

      // Force the real selling_plan id into the request body ourselves
      // rather than trusting whatever's on the form's hidden input at this
      // point - a subscription app's embed script (e.g. Seal Subscriptions)
      // can also be watching this same form and takes over/clears that
      // field for its own bookkeeping, which would otherwise silently
      // downgrade a subscription order to one-time. See
      // ProductPage.getSellingPlanId() in product.js.
      const productPage = form.closest('product-page');
      if (productPage && typeof productPage.getSellingPlanId === 'function') {
        const sellingPlanId = productPage.getSellingPlanId();
        if (sellingPlanId) {
          formData.set('selling_plan', sellingPlanId);
        } else {
          formData.delete('selling_plan');
        }
      }

      await fetch('/cart/add.js', { method: 'POST', body: formData });
    } finally {
      document.dispatchEvent(new CustomEvent('cart:loading-end'));
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { skipOpen } }));
      document.dispatchEvent(new CustomEvent('cart:changed'));
    }
  });

  // With the drawer kept closed, pulse the header's cart count instead so the
  // add still registers. Waits for the refresh so the new count (and a count
  // that was hidden at zero) is showing before it animates.
  let pulsePending = false;
  document.addEventListener('cart:added', (event) => {
    if (event.detail?.skipOpen) pulsePending = true;
  });
  document.addEventListener('cart:refreshed', () => {
    if (!pulsePending) return;
    pulsePending = false;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.classList.remove('is-pulsing');
      void el.offsetWidth; // restart the animation on back-to-back adds
      el.classList.add('is-pulsing');
      el.addEventListener('animationend', () => el.classList.remove('is-pulsing'), { once: true });
    });
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-cart-drawer-toggle]')) {
      event.preventDefault();
      document.dispatchEvent(new CustomEvent('cart:open'));
    }
  });
}

// A cart line's properties, from the data-properties attribute the cart
// snippets render with `item.properties | json`. Liquid serialises them as an
// array of [name, value] pairs (`[]` when there are none), but /cart/add.js
// only accepts an object and rejects the whole add (400) otherwise - so the
// pairs are turned back into one here.
function parseLineProperties(json) {
  let parsed;
  try {
    parsed = JSON.parse(json || '{}');
  } catch (error) {
    return {};
  }
  if (Array.isArray(parsed)) {
    return Object.fromEntries(parsed.filter((pair) => Array.isArray(pair) && pair.length === 2));
  }
  return parsed && typeof parsed === 'object' ? parsed : {};
}

class FlyoutCart extends HTMLElement {
  connectedCallback() {
    bindGlobalCartListeners();

    // An inline instance (data-inline) is a permanently visible cart panel
    // embedded in page content - e.g. a "shop all" sticky sidebar - rather
    // than a fixed overlay dialog. It shares all the same item interactions
    // (qty, remove, refresh) but never hides/shows itself.
    this.inline = this.hasAttribute('data-inline');
    this.panel = this.querySelector('.flyout-cart__panel') || this;
    this.lastFocused = null;

    this.checkFreeGift(Number(this.dataset.cartTotalPrice), this.readCartItems());

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-drawer-close]')) this.close();
      if (event.target.closest('[data-cart-qty-increase]')) this.changeQuantity(event, 1);
      if (event.target.closest('[data-cart-qty-decrease]')) this.changeQuantity(event, -1);
      if (event.target.closest('[data-cart-remove]')) this.removeLine(event);
      if (event.target.closest('[data-cart-clear]')) this.clearCart();
      const subscribeBtn = event.target.closest('[data-cart-subscribe]');
      if (subscribeBtn) this.changeSellingPlan(subscribeBtn, subscribeBtn.dataset.planId);
      if (event.target.closest('[data-bundle-remove]')) this.removeBundle(event);
      if (event.target.closest('[data-bundle-edit]')) this.editBundle(event);
      const bundleSubscribeBtn = event.target.closest('[data-bundle-subscribe]');
      if (bundleSubscribeBtn) this.changeBundleFrequency(bundleSubscribeBtn, bundleSubscribeBtn.dataset.frequency);
      const bundleUpgradeBtn = event.target.closest('[data-bundle-upgrade]');
      if (bundleUpgradeBtn) this.upgradeToBundle(bundleUpgradeBtn);
      if (event.target.closest('[data-gift-confirm-keep]')) this.cancelGiftRemoval();
      if (event.target.closest('[data-gift-confirm-remove]')) this.confirmGiftRemoval();
    });

    if (!this.inline) {
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || this.hidden) return;
        if (this.isGiftConfirmOpen()) {
          this.cancelGiftRemoval();
        } else {
          this.close();
        }
      });
    }

    // Order note: saved 400ms after typing stops, immediately when the field
    // loses focus, and always before checkout / a drawer re-render (see
    // flushNote()), so a quick "type then click Checkout" can't lose it.
    this.noteDebounce = null;
    this.pendingNote = undefined;
    this.noteQueue = Promise.resolve();
    this.notesInFlight = 0;
    this.addEventListener('input', (event) => {
      if (!event.target.matches('[data-cart-note]')) return;
      this.updateNoteCount(event.target);
      this.pendingNote = event.target.value;
      clearTimeout(this.noteDebounce);
      this.noteDebounce = setTimeout(() => this.flushNote(), 400);
    });

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-note-save]')) this.saveNote();
      if (event.target.closest('[data-cart-note-edit]')) this.setNoteEditorOpen(true, true);

      const checkout = event.target.closest('.flyout-cart__checkout-btn');
      if (!checkout || !this.hasUnsavedNote()) return;
      event.preventDefault();
      this.flushNote().finally(() => {
        window.location.href = checkout.href;
      });
    });

    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-cart-note]')) this.flushNote();
      if (event.target.matches('[data-cart-note-toggle]')) this.toggleNote(event.target);
      if (event.target.matches('[data-cart-variant-select]')) this.changeVariant(event.target);
      if (event.target.matches('[data-cart-plan-select]')) this.changeSellingPlan(event.target, event.target.value);
      if (event.target.matches('[data-bundle-plan-select]')) this.changeBundleFrequency(event.target, event.target.value);
    });

    document.addEventListener('cart:open', () => this.open());

    document.addEventListener('cart:loading-start', () => this.classList.add('loading'));
    document.addEventListener('cart:loading-end', () => this.classList.remove('loading'));

    document.addEventListener('cart:changed', async () => {
      await this.refresh();
    });

    document.addEventListener('cart:added', (event) => {
      if (!this.inline && !event.detail?.skipOpen) this.open();
    });
  }

  open() {
    if (this.inline) return;
    this.lastFocused = document.activeElement;
    this.hidden = false;
    document.body.style.overflow = 'hidden';
    this.panel.focus();
  }

  close() {
    if (this.inline) return;
    this.hidden = true;
    document.body.style.overflow = '';
    if (this.lastFocused) this.lastFocused.focus();
  }

  async changeQuantity(event, delta) {
    const line = Number(event.target.closest('[data-line]').dataset.line);
    const valueEl = event.target.closest('.flyout-cart__item-qty').querySelector('[data-cart-qty-value]');
    const quantity = Math.max(0, Number(valueEl.textContent) + delta);
    await this.updateLine(line, quantity);
  }

  async removeLine(event) {
    const li = event.target.closest('[data-line]');
    const line = Number(li.dataset.line);
    const giftProductId = this.dataset.freeGiftProductId;

    if (giftProductId && li.dataset.productId === giftProductId) {
      this.pendingGiftLine = line;
      this.toggleGiftConfirm(true);
      return;
    }

    await this.updateLine(line, 0);
  }

  async clearCart() {
    document.dispatchEvent(new CustomEvent('cart:loading-start'));
    try {
      await fetch('/cart/clear.js', { method: 'POST' });
    } finally {
      document.dispatchEvent(new CustomEvent('cart:loading-end'));
      document.dispatchEvent(new CustomEvent('cart:changed'));
    }
  }

  // --- bundles -----------------------------------------------------------
  // Lines added by build-a-bundle share a `_bundle_id` line item property and
  // are rendered as one grouped card (snippets/cart-drawer-bundle.liquid).

  async removeBundleLines(keys) {
    const updates = {};
    keys.forEach((key) => {
      updates[key] = 0;
    });
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });
    return response.ok;
  }

  async removeBundle(event) {
    const group = event.target.closest('[data-bundle-group]');
    if (!group) return;
    const keys = Array.from(group.querySelectorAll('[data-item-key]')).map((el) => el.dataset.itemKey);
    if (keys.length === 0) return;

    document.dispatchEvent(new CustomEvent('cart:loading-start'));
    try {
      await this.removeBundleLines(keys);
    } finally {
      document.dispatchEvent(new CustomEvent('cart:loading-end'));
      document.dispatchEvent(new CustomEvent('cart:changed'));
    }
  }

  // Turns the bundle's cart lines back into an in-progress bundle: the lines
  // are read from the live cart by `_bundle_id`, parked in sessionStorage for
  // <bundle-builder> to pick up, removed from the cart (so re-adding can't
  // duplicate them), then the customer is sent back to the bundle page.
  async editBundle(event) {
    const group = event.target.closest('[data-bundle-group]');
    if (!group) return;
    const bundleId = group.dataset.bundleId;
    const bundleUrl = group.dataset.bundleUrl;
    if (!bundleId || !bundleUrl) return;

    await this.moveLinesToBundle(
      (item) => item.properties && item.properties._bundle_id === bundleId,
      bundleUrl,
    );
  }

  // "Make it a bundle" prompt (cart-drawer-items.liquid, flyout-cart's
  // "Show bundle upgrade prompt"): the same handoff as Edit, for every loose
  // line - anything not already in a bundle, minus free gifts.
  async upgradeToBundle(button) {
    const bundleUrl = button.dataset.bundleUrl;
    if (!bundleUrl) return;

    const errorEl = button.parentElement?.querySelector('[data-bundle-upgrade-error]');
    if (errorEl) errorEl.hidden = true;

    // Loose lines don't carry their selling plans over: one subscribed
    // product would otherwise turn the whole new bundle into a subscription,
    // overriding the bundle's one-time default without the customer choosing it.
    const moved = await this.moveLinesToBundle(
      (item) => !(item.properties && item.properties._bundle_id) && item.final_price > 0,
      bundleUrl,
      { keepPlans: false },
    );
    // A failed attempt re-renders the drawer - refresh() shows the error on
    // the new markup (it sets this before the refresh's fetch resolves).
    if (!moved) this.pendingUpgradeError = true;
  }

  // Parks the cart lines matching `filter` in sessionStorage for
  // <bundle-builder> (adoptPendingEdit() in build-a-bundle.js), removes them
  // from the cart so re-adding can't duplicate them, then navigates to the
  // bundle page. Resolves false (and refreshes the drawer) if anything fails.
  // With `keepPlans: false` the lines' selling plans are dropped, so the
  // builder opens on its default purchase type instead of the lines' plan.
  async moveLinesToBundle(filter, bundleUrl, { keepPlans = true } = {}) {
    document.dispatchEvent(new CustomEvent('cart:loading-start'));
    let redirect = false;
    try {
      const cart = await (await fetch('/cart.js')).json();
      const lines = cart.items.filter(filter);
      if (lines.length === 0) return false;

      try {
        let items = this.toBundleItems(lines);
        if (!keepPlans) items = items.map((item) => ({ ...item, sellingPlanId: null }));
        sessionStorage.setItem('bundle:pending-edit', JSON.stringify(items));
      } catch (error) {
        return false;
      }

      const removed = await this.removeBundleLines(lines.map((item) => item.key));
      if (!removed) {
        sessionStorage.removeItem('bundle:pending-edit');
        return false;
      }
      redirect = true;
      return true;
    } catch (error) {
      return false;
    } finally {
      document.dispatchEvent(new CustomEvent('cart:loading-end'));
      if (redirect) {
        window.location.href = bundleUrl;
      } else {
        document.dispatchEvent(new CustomEvent('cart:changed'));
      }
    }
  }

  // Cart lines -> <bundle-builder> state items. Loose lines can hold the
  // same variant twice (one-time and subscribed); the builder keeps one
  // entry per variant, so those are merged.
  toBundleItems(lines) {
    const items = new Map();
    lines.forEach((item) => {
      const variantId = String(item.variant_id);
      const sellingPlanId = item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.id : null;
      const existing = items.get(variantId);
      if (existing) {
        existing.quantity += item.quantity;
        if (!existing.sellingPlanId) existing.sellingPlanId = sellingPlanId;
        return;
      }
      items.set(variantId, {
        variantId,
        productId: String(item.product_id),
        quantity: item.quantity,
        title: item.product_title,
        variantTitle: item.variant_title || '',
        image: item.featured_image ? item.featured_image.url : item.image || '',
        url: item.url,
        // Base (one-time) price - the bundle re-derives the subscription
        // price from its own selling plan data. For a plan line, the
        // allocation's compare_at_price is the variant's one-time price.
        price: (item.selling_plan_allocation && item.selling_plan_allocation.compare_at_price) || item.price,
        sellingPlanId,
      });
    });
    return Array.from(items.values());
  }

  isGiftConfirmOpen() {
    const overlay = this.querySelector('[data-gift-confirm]');
    return Boolean(overlay && !overlay.hidden);
  }

  toggleGiftConfirm(show) {
    const overlay = this.querySelector('[data-gift-confirm]');
    if (!overlay) return;
    overlay.hidden = !show;
    if (show) overlay.querySelector('[data-gift-confirm-keep]').focus();
  }

  cancelGiftRemoval() {
    this.pendingGiftLine = null;
    this.toggleGiftConfirm(false);
  }

  async confirmGiftRemoval() {
    const giftProductId = this.dataset.freeGiftProductId;
    if (giftProductId) sessionStorage.setItem(`freeGiftDeclined:${giftProductId}`, 'true');

    this.toggleGiftConfirm(false);

    if (this.pendingGiftLine != null) {
      const line = this.pendingGiftLine;
      this.pendingGiftLine = null;
      await this.updateLine(line, 0);
    }
  }

  async updateLine(line, quantity) {
    document.dispatchEvent(new CustomEvent('cart:loading-start'));
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
    } finally {
      document.dispatchEvent(new CustomEvent('cart:loading-end'));
      document.dispatchEvent(new CustomEvent('cart:changed'));
    }
  }

  // Moves a line onto a different selling plan - used by both the frequency
  // dropdown on subscription lines and the "Subscribe & save" upsell button
  // on one-time lines (see switchLinePlan()). "One-time purchase" in the
  // dropdown (`__one_time`) takes the line off its plan.
  async changeSellingPlan(control, sellingPlanId) {
    if (!control.dataset.itemKey || !sellingPlanId || sellingPlanId === control.dataset.currentPlan) return;
    if (sellingPlanId === '__one_time') sellingPlanId = null;

    const errorEl = control.closest('[data-line]')?.querySelector('[data-cart-plan-error]');
    if (errorEl) errorEl.hidden = true;

    document.dispatchEvent(new CustomEvent('cart:loading-start'));
    let changed = false;
    try {
      changed = await this.switchLinePlan(control, sellingPlanId);
    } catch (error) {
      // Network failure - handled in finally.
    } finally {
      document.dispatchEvent(new CustomEvent('cart:loading-end'));
      if (changed) {
        document.dispatchEvent(new CustomEvent('cart:changed'));
      } else {
        if (control.matches('select') && control.dataset.currentPlan) control.value = control.dataset.currentPlan;
        if (errorEl) errorEl.hidden = false;
      }
    }
  }

  // `/cart/change.js` can switch a line's plan in place (keeping its
  // position); if Shopify rejects that, fall back to the same add-then-remove
  // swap changeVariant() uses, so the original line is only removed once the
  // new one has been added. `el` carries the line's data-item-key,
  // data-quantity and (for the fallback) data-variant-id + data-properties.
  // A null `sellingPlanId` takes the line off its plan (one-time purchase).
  async switchLinePlan(el, sellingPlanId) {
    const key = el.dataset.itemKey;
    const quantity = Number(el.dataset.quantity) || 1;
    const planId = sellingPlanId == null ? null : Number(sellingPlanId);

    const changeResponse = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity, selling_plan: planId }),
    });
    if (changeResponse.ok) return true;
    if (!el.dataset.variantId) return false;

    const properties = parseLineProperties(el.dataset.properties);
    const newLine = { id: Number(el.dataset.variantId), quantity, properties };
    if (planId != null) newLine.selling_plan = planId;
    const addResponse = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [newLine] }),
    });
    if (!addResponse.ok) return false;

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: { [key]: 0 } }),
    });
    return true;
  }

  // Moves a bundle onto the chosen frequency - from the frequency dropdown on
  // a subscribed bundle, or the "Subscribe & save" button on a one-time one.
  // Each product has its own plan ids, so each line looks up its own plan by
  // frequency key (see cart-drawer-bundle.liquid); lines without that
  // frequency (one-time-only products) are left as they are. Lines are
  // changed one at a time - changing one line's plan never changes another
  // line's key.
  async changeBundleFrequency(control, frequencyKey) {
    const group = control.closest('[data-bundle-group]');
    if (!group || !frequencyKey || frequencyKey === control.dataset.currentPlan) return;

    const errorEl = group.querySelector('[data-cart-plan-error]');
    if (errorEl) errorEl.hidden = true;

    // "One-time purchase" in the dropdown takes every subscribed line off
    // its plan (switchLinePlan() with a null plan).
    const toOneTime = frequencyKey === '__one_time';

    const changes = [];
    group.querySelectorAll('[data-plans]').forEach((line) => {
      if (toOneTime) {
        if (line.dataset.sellingPlan) changes.push({ line, planId: null });
        return;
      }
      let plans = [];
      try {
        plans = JSON.parse(line.dataset.plans || '[]');
      } catch (error) {
        plans = [];
      }
      const plan = plans.find((p) => p.key === frequencyKey);
      if (plan && String(plan.id) !== line.dataset.sellingPlan) changes.push({ line, planId: plan.id });
    });
    if (changes.length === 0) return;

    document.dispatchEvent(new CustomEvent('cart:loading-start'));
    let failed = false;
    try {
      for (const change of changes) {
        if (!(await this.switchLinePlan(change.line, change.planId))) failed = true;
      }
    } catch (error) {
      failed = true;
    } finally {
      document.dispatchEvent(new CustomEvent('cart:loading-end'));
      // The refresh replaces the body, so a failure is flagged on the
      // re-rendered bundle by refresh() itself (see pendingBundleError).
      if (failed) this.pendingBundleError = group.dataset.bundleId;
      // Refresh regardless so the drawer shows what the cart really holds,
      // even if only some lines switched.
      document.dispatchEvent(new CustomEvent('cart:changed'));
    }
  }

  // Shopify can't change a line's variant in place, so the new variant is
  // added (same quantity, properties and selling plan) and only once that
  // succeeds is the old line removed - by key, since adding can shift line
  // indexes. If the add is rejected (out of stock, selling plan not offered
  // on that variant...) the original line is left untouched.
  async changeVariant(select) {
    const variantId = select.value;
    const previousId = select.dataset.currentVariantId;
    const key = select.dataset.itemKey;
    if (!variantId || !key || variantId === previousId) return;

    const errorEl = select.closest('[data-line]')?.querySelector('[data-cart-variant-error]');
    if (errorEl) errorEl.hidden = true;

    const properties = parseLineProperties(select.dataset.properties);
    const item = { id: Number(variantId), quantity: Number(select.dataset.quantity) || 1, properties };
    if (select.dataset.sellingPlan) item.selling_plan = Number(select.dataset.sellingPlan);

    document.dispatchEvent(new CustomEvent('cart:loading-start'));
    let added = false;
    try {
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });
      if (addResponse.ok) {
        added = true;
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: { [key]: 0 } }),
        });
      }
    } catch (error) {
      // Network failure - handled in finally.
    } finally {
      document.dispatchEvent(new CustomEvent('cart:loading-end'));
      // Refresh whenever the add went through (even if removing the old line
      // failed) so the drawer always shows what's really in the cart.
      if (added) {
        document.dispatchEvent(new CustomEvent('cart:changed'));
      } else {
        select.value = previousId;
        if (errorEl) errorEl.hidden = false;
      }
    }
  }

  // Ticking "Add a note" opens the editor; unticking clears and saves an
  // empty note.
  toggleNote(checkbox) {
    const textarea = this.querySelector('[data-cart-note]');
    if (!textarea) return;

    if (checkbox.checked) {
      this.setNoteEditorOpen(true, true);
    } else {
      textarea.value = '';
      this.updateNoteCount(textarea);
      this.pendingNote = '';
      this.flushNote();
      this.setNoteEditorOpen(false, false);
    }
  }

  // Shows the editor (textarea + Save) or collapses it. While collapsed with
  // a note saved, "Edit note" sits on the right to reopen it.
  setNoteEditorOpen(open, moveFocus) {
    const editor = this.querySelector('[data-cart-note-editor]');
    const textarea = this.querySelector('[data-cart-note]');
    const editBtn = this.querySelector('[data-cart-note-edit]');
    if (!editor || !textarea) return;

    editor.hidden = !open;
    if (editBtn) {
      editBtn.hidden = open || textarea.value.trim() === '';
      editBtn.setAttribute('aria-expanded', String(open));
    }

    if (!moveFocus) return;
    if (open) {
      textarea.focus();
    } else if (editBtn && !editBtn.hidden) {
      editBtn.focus();
    }
  }

  saveNote() {
    const textarea = this.querySelector('[data-cart-note]');
    if (!textarea) return;

    this.pendingNote = textarea.value;
    this.flushNote();

    // Saving an empty note is the same as unticking the box.
    if (textarea.value.trim() === '') {
      textarea.value = '';
      const checkbox = this.querySelector('[data-cart-note-toggle]');
      if (checkbox) checkbox.checked = false;
      this.setNoteEditorOpen(false, false);
      checkbox?.focus();
      return;
    }

    this.setNoteEditorOpen(false, true);
  }

  // "37/100" under the note - the textarea's maxlength does the limiting.
  updateNoteCount(textarea) {
    const count = this.querySelector('[data-cart-note-count]');
    if (count) count.textContent = `${textarea.value.length}/${textarea.maxLength}`;
  }

  hasUnsavedNote() {
    return this.pendingNote !== undefined || this.notesInFlight > 0;
  }

  // Sends any pending note now and resolves once every note save so far has
  // finished. Saves are chained so they reach Shopify in the order typed -
  // an older, slower request can't overwrite a newer note.
  flushNote() {
    clearTimeout(this.noteDebounce);
    this.noteDebounce = null;

    if (this.pendingNote !== undefined) {
      const note = this.pendingNote;
      this.pendingNote = undefined;
      this.notesInFlight += 1;
      this.noteQueue = this.noteQueue
        .then(() => this.updateNote(note))
        .finally(() => {
          this.notesInFlight -= 1;
        });
    }
    return this.noteQueue;
  }

  async updateNote(note) {
    try {
      // keepalive lets the request finish even if the page navigates away
      // (e.g. an express checkout button) while it is in flight.
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
        keepalive: true,
      });
      if (!response.ok) console.error('[theme] cart note failed to save', response.status);
    } catch (error) {
      console.error('[theme] cart note failed to save', error);
    }
  }

  async refresh() {
    // The footer (and its note field) is about to be replaced with fresh
    // server HTML - save the note first so the new markup includes it.
    if (this.hasUnsavedNote()) await this.flushNote();

    const sectionId = this.dataset.sectionId || this.id;
    const url = `${window.location.pathname}?section_id=${sectionId}`;
    const response = await fetch(url);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const newRewards = doc.querySelector('[data-cart-drawer-rewards]');
    const newBody = doc.querySelector('[data-cart-drawer-body]');
    const newFooter = doc.querySelector('[data-cart-drawer-footer]');
    const newRoot = doc.querySelector(`#${CSS.escape(this.id)}`) || doc.querySelector('flyout-cart');

    const currentRewards = this.querySelector('[data-cart-drawer-rewards]');
    if (newRewards && currentRewards) currentRewards.outerHTML = newRewards.outerHTML;
    if (newBody) this.querySelector('[data-cart-drawer-body]').innerHTML = newBody.innerHTML;
    // The server always renders the note editor collapsed - keep it open if
    // the customer was mid-edit when the cart changed.
    const noteEditorWasOpen = this.querySelector('[data-cart-note-editor]')?.hidden === false;
    if (newFooter) this.querySelector('[data-cart-drawer-footer]').outerHTML = newFooter.outerHTML;
    if (noteEditorWasOpen) this.setNoteEditorOpen(true, false);

    if (this.pendingBundleError) {
      const bundle = this.querySelector(`[data-bundle-group][data-bundle-id="${CSS.escape(this.pendingBundleError)}"]`);
      const bundleError = bundle?.querySelector('[data-cart-plan-error]');
      if (bundleError) bundleError.hidden = false;
      this.pendingBundleError = null;
    }

    if (this.pendingUpgradeError) {
      const upgradeError = this.querySelector('[data-bundle-upgrade-error]');
      if (upgradeError) upgradeError.hidden = false;
      this.pendingUpgradeError = false;
    }

    if (this.pendingGiftAnimation) {
      this.pendingGiftAnimation = false;
      this.playGiftAnimation();
    }

    const itemCount = newRoot ? Number(newRoot.dataset.itemCount) : 0;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = itemCount;
      el.hidden = itemCount === 0;
    });

    // These sit outside the re-rendered body/footer (express checkout buttons
    // must never be re-rendered - Shopify only initialises them once), so
    // they are only shown/hidden to match the item count.
    this.querySelectorAll('.flyout-cart__header [data-cart-clear], [data-cart-express]').forEach((el) => {
      el.hidden = itemCount === 0;
    });

    const newTotal = doc.querySelector('[data-cart-total-display]');
    if (newTotal) {
      document.querySelectorAll('[data-cart-total-display]').forEach((el) => {
        el.textContent = newTotal.textContent;
      });
    }

    document.dispatchEvent(new CustomEvent('cart:refreshed'));

    if (newRoot && newBody) {
      await this.checkFreeGift(Number(newRoot.dataset.cartTotalPrice), this.readCartItems(newBody));
    }
  }

  readCartItems(root = this) {
    return Array.from(root.querySelectorAll('[data-product-id]')).map((el) => ({
      productId: el.dataset.productId,
      line: Number(el.dataset.line),
    }));
  }

  async checkFreeGift(totalPrice, cartItems) {
    if (this.dataset.freeGiftEnabled !== 'true') return;

    const threshold = Number(this.dataset.freeGiftThreshold);
    const variantId = this.dataset.freeGiftVariantId;
    const productId = this.dataset.freeGiftProductId;
    if (!variantId || !productId || Number.isNaN(totalPrice)) return;

    const giftItem = cartItems.find((item) => item.productId === productId);

    if (totalPrice < threshold) {
      // Cart fell back below the threshold - remove the gift, but don't mark
      // it as declined, since the customer didn't choose to remove it.
      if (giftItem) await this.updateLine(giftItem.line, 0);
      return;
    }

    if (giftItem) return;
    if (sessionStorage.getItem(`freeGiftDeclined:${productId}`) === 'true') return;

    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    });
    this.pendingGiftAnimation = true;
    document.dispatchEvent(new CustomEvent('cart:changed'));
  }

  playGiftAnimation() {
    const animation = this.dataset.freeGiftAnimation;
    if (!animation || animation === 'none') return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const giftEl = this.querySelector('[data-cart-drawer-body] [data-free-gift]');
    if (!giftEl) return;

    if (animation === 'fade') {
      giftEl.classList.remove('flyout-cart__item--gift-fade');
      void giftEl.offsetWidth;
      giftEl.classList.add('flyout-cart__item--gift-fade');
      setTimeout(() => giftEl.classList.remove('flyout-cart__item--gift-fade'), 4300);
    } else if (animation === 'pop') {
      this.spawnGiftConfetti();
    }
  }

  spawnGiftConfetti() {
    const body = this.querySelector('[data-cart-drawer-body]');
    if (!body) return;

    body.querySelectorAll('.flyout-cart__confetti').forEach((el) => el.remove());

    const container = document.createElement('div');
    container.className = 'flyout-cart__confetti';
    container.setAttribute('aria-hidden', 'true');
    body.prepend(container);

    const colors = ['#f97316', '#ef4444', '#3b82f6', '#22c55e', '#eab308', '#a855f7', '#ec4899'];
    const perSide = 35;

    ['left', 'right'].forEach((side) => {
      const inward = side === 'left' ? 1 : -1;

      for (let i = 0; i < perSide; i += 1) {
        const piece = document.createElement('span');
        piece.className = 'flyout-cart__confetti-piece';
        piece.style[side] = '-4px';
        piece.style.background = colors[Math.floor(Math.random() * colors.length)];

        // Cone-shaped burst: random spread + height simulate the initial
        // velocity of a confetti cannon, then gravity pulls pieces back down.
        const spreadX = 60 + Math.random() * 200;
        const peakY = -(110 + Math.random() * 170);
        const fallY = 220 + Math.random() * 200;
        const driftX = (Math.random() - 0.5) * 60;

        piece.style.setProperty('--peak-x', `${inward * spreadX}px`);
        piece.style.setProperty('--peak-y', `${peakY}px`);
        piece.style.setProperty('--end-x', `${inward * spreadX + driftX}px`);
        piece.style.setProperty('--end-y', `${peakY + fallY}px`);
        piece.style.setProperty('--rotate', `${Math.random() * 720 - 360}deg`);
        piece.style.setProperty('--duration', `${1.8 + Math.random() * 0.9}s`);
        piece.style.setProperty('--delay', `${Math.random() * 0.25}s`);

        container.appendChild(piece);
      }
    });

    setTimeout(() => container.remove(), 3400);
  }
}

// Guarded because this file can legitimately be requested by more than one
// section on the same page (e.g. the header's overlay drawer plus a
// section-level inline cart) - re-registering the same tag name throws.
if (!customElements.get('flyout-cart')) {
  customElements.define('flyout-cart', FlyoutCart);
}

})();
